import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {Badge} from "@/components/ui/badge";
import {SlidersHorizontal} from "lucide-react";
import {useMemo} from "react";

import {TruncatedWithTooltip} from "@/src/modules/core/components";
import {cn} from "@/lib/utils";

import {
  DispatchDeskCardShell,
  DispatchDeskEmptyMessage,
  DispatchDeskEmptyPanelBody,
  DispatchDeskNestedList,
  DispatchDeskScrollRegion,
  DISPATCH_DESK_NESTED_INDENT_CLASS,
  DISPATCH_DESK_CONTENT_MUTED_CLASS,
  DISPATCH_DESK_ROW_PRIMARY_CLASS,
  DISPATCH_DESK_ROW_SECONDARY_CLASS,
} from "./dispatch-desk-card";
import {useDispatchDeskProperty} from "../hooks/use-dispatch-desk-property";
import {getDispatchFeatureIcon} from "../lib/dispatch-feature-icons";
import {
  buildDispatchPropertyRuleGroups,
  type DispatchPropertyRuleRow,
} from "../lib/dispatch-property-rules";
import {
  DISPATCH_DESK_CAPPED_CARD_CONTENT_CLASS,
  DISPATCH_DESK_MOBILE_TALL_PANEL_CAP_CLASS,
  DISPATCH_DESK_NO_PROPERTY_MESSAGE,
  DISPATCH_DESK_PANEL_CLASS,
} from "../lib/dispatch-desk-layout";

type DispatchPropertyRulesProps = {
  className?: string;
};

const RULES_ACCORDION_TRIGGER_CLASS =
  "rounded-none border-0 py-3 font-normal hover:no-underline focus-visible:ring-0 [&[data-open]>svg]:rotate-180";

function RuleValue({value}: {value: string}) {
  if (value === "Yes" || value === "No") {
    return (
      <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-xs font-normal">
        {value}
      </Badge>
    );
  }

  return (
    <span className={cn("shrink-0 text-right font-normal tabular-nums", DISPATCH_DESK_ROW_SECONDARY_CLASS)}>
      {value}
    </span>
  );
}

function PropertyRuleRow({rule}: {rule: DispatchPropertyRuleRow}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <TruncatedWithTooltip
          content={rule.label}
          maxLines={2}
          className={DISPATCH_DESK_ROW_PRIMARY_CLASS}
        >
          {rule.label}
        </TruncatedWithTooltip>
      </div>
      <RuleValue value={rule.value} />
    </div>
  );
}

export function DispatchPropertyRules({className}: DispatchPropertyRulesProps) {
  const property = useDispatchDeskProperty();
  const groups = useMemo(
    () => (property ? buildDispatchPropertyRuleGroups(property) : []),
    [property],
  );

  return (
    <DispatchDeskCardShell
      className={cn(
        DISPATCH_DESK_PANEL_CLASS,
        DISPATCH_DESK_MOBILE_TALL_PANEL_CAP_CLASS,
        className,
      )}
      contentClassName={DISPATCH_DESK_CAPPED_CARD_CONTENT_CLASS}
      icon={SlidersHorizontal}
      iconClassName="text-primary"
      title="Property rules"
    >
      <DispatchDeskScrollRegion capped>
        {!property ? (
          <DispatchDeskEmptyPanelBody>
            <DispatchDeskEmptyMessage>{DISPATCH_DESK_NO_PROPERTY_MESSAGE}</DispatchDeskEmptyMessage>
          </DispatchDeskEmptyPanelBody>
        ) : groups.length === 0 ? (
          <DispatchDeskEmptyPanelBody>
            <DispatchDeskEmptyMessage>No property rules on file.</DispatchDeskEmptyMessage>
          </DispatchDeskEmptyPanelBody>
        ) : (
          <Accordion multiple className={cn("w-full min-w-0", DISPATCH_DESK_NESTED_INDENT_CLASS)}>
            {groups.map((group, index) => {
              const FeatureIcon = getDispatchFeatureIcon(group.featureKey);

              return (
              <AccordionItem
                key={group.featureKey}
                value={group.featureKey}
                className={cn("border-b-0 not-last:border-b-0", index > 0 && "border-border border-t")}
              >
                <AccordionTrigger
                  className={RULES_ACCORDION_TRIGGER_CLASS}
                  headerClassName="w-full"
                >
                  <span className="flex min-w-0 flex-1 items-center gap-1.5">
                    <FeatureIcon
                      className="text-muted-foreground size-3 shrink-0"
                      aria-hidden
                    />
                    <span className={cn("min-w-0 truncate", DISPATCH_DESK_ROW_PRIMARY_CLASS)}>
                      {group.featureLabel}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-3">
                  <DispatchDeskNestedList>
                    {group.rules.length === 0 ? (
                      <p className={DISPATCH_DESK_CONTENT_MUTED_CLASS}>
                        No rules on file for this feature.
                      </p>
                    ) : (
                      group.rules.map((rule) => (
                        <PropertyRuleRow key={rule.key} rule={rule} />
                      ))
                    )}
                  </DispatchDeskNestedList>
                </AccordionContent>
              </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </DispatchDeskScrollRegion>
    </DispatchDeskCardShell>
  );
}
